import React, { useState } from 'react';
import { Option, Supplier } from 'src/types';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import ApiClient from 'src/lib/api_client';
import pluralize from 'pluralize';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import { BsStarFill } from 'react-icons/bs';

const ValidationSchema = yup.object().shape({
  email: yup
    .string()
    .required('Please enter your email')
    .email('Email is invalid')
    .typeError('Please enter your email'),
});

type Props = {
  option: Option;
  supplier: Supplier;
  setShowReviewModal?: React.Dispatch<React.SetStateAction<boolean>>;
};

const WaitlistForm: React.FC<Props> = ({ option, supplier, setShowReviewModal }) => {
  const joinWaitlist = useEnhanceApiClient(ApiClient.joinWaitlist)
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  return (
    <>
      <div className="tw-text-xl tw-font-semibold tw-leading-none tw-pb-6 tw-mb-6 tw-border-b tw-border-grey-lighter tw-flex align-items-center justify-content-between">
        <h2 className="tw-text-xl tw-font-semibold tw-font-styleSans tw-leading-none mb-0">
          Join Waitlist
        </h2>
        {supplier.ratedReviewsCount > 0 && (
          <div className="tw-cursor-pointer tw-font-semibold tw-font-styleSans tw-flex tw-items-center" onClick={() => setShowReviewModal && setShowReviewModal(true)}>
            <BsStarFill size={16} className="tw-text-orange tw-align-middle tw-mb-1 tw-mr-1" />
            <span className="tw-text-base tw-text-brand-navy tw-mr-1 tw-leading-none">
              {supplier.reviewRating}
            </span>
            <span className="tw-text-base tw-text-brand-navy tw-leading-none">
              ({pluralize('review', supplier.ratedReviewsCount)})
            </span>
          </div>
        )}
      </div>
      <Formik
        initialValues={{
          email: '',
          supplierActivityOptionId: option.id,
        }}
        validationSchema={ValidationSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          joinWaitlist(values)
            .then((result) => {
              const [json] = result
              if ('error' in json) {
                setError(true);
                setSuccess(false);
              } else {
                setSuccess(true);
                resetForm();
              }
            })
            .finally(() => setSubmitting(false));
        }}
      >
        {(formik) => (
          <Form>
            <div className="tw-mb-4">
              This experience is taking a break, but join the waitlist to snag a spot when it{"'"}s back!
            </div>

            <div className="form-group">
              <Field name="email" className="form-control" placeholder="Email address" />
              <ErrorMessage name="email" component="div" className="invalid-feedback tw-block" />
            </div>

            {success && (
              <div className="form-group tw-text-green">You’ve been added to the waitlist!</div>
            )}
            {error && (
              <div className="form-group tw-text-red">
                An internal error occured! Please contact us for further support.
              </div>
            )}

            <div className="form-group tw-text-center tw-mt-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={formik.isSubmitting}
              >
                Join Waitlist
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default WaitlistForm;
